import { useCallback, useEffect } from 'react';
import { CurrencyInput, DocumentNumberField, FormField, SearchField } from '@/components/custom/erp';
import { useButToanDieuChinhGiamCongNo } from '@/hooks/queries/useButToanDieuChinhGiamCongNo';
import { chungTuKeToanSearchColumns, MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useChungTuPhaiTraKhac } from '@/hooks/queries/useChungTuPhaiTraKhac';
import { usePhieuKeToan } from '@/hooks/queries/usePhieuKeToan';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { isValidUUID } from '@/lib/uuid-validator';
import { useAuth } from '@/contexts/auth-context';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';
import api from '@/lib/api';

const GetEndPoints = [
  {
    ma_ct: 'CN5',
    endPoint: QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC
  },
  {
    ma_ct: 'CN6',
    endPoint: QUERY_KEYS.BUT_TOAN_DIEU_CHINH_GIAM_CONG_NO_MH
  },
  {
    ma_ct: 'CN2',
    endPoint: QUERY_KEYS.BUT_TOAN_DIEU_CHINH_GIAM_CONG_NO
  },
  {
    ma_ct: 'PK1',
    endPoint: QUERY_KEYS.PHIEU_KE_TOAN
  }
];

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
  onPopulateDetailRows?: (data: any[]) => void;
}

export function BasicInfoTab({ formMode, formState: { state, actions }, onPopulateDetailRows }: BasicInfoTabProps) {
  const { entity } = useAuth();
  const { getPhieuKeToanDetail } = usePhieuKeToan();
  const { getChungTuPhaiTraKhacDetail } = useChungTuPhaiTraKhac();
  const { getButToanDieuChinhGiamCongNoDetail } = useButToanDieuChinhGiamCongNo();

  const handleExchangeRateChange = useCallback(
    (exchangeRate: number | null) => {
      if (exchangeRate) {
        actions.setTyGia(exchangeRate);
      }
    },
    [actions]
  );

  const loadChungTuGocDetails = useCallback(
    async (chungTuGoc: any, idGoc?: string) => {
      if (!chungTuGoc?.ma_ct || !entity?.slug) return;

      try {
        actions.setChungTuChiTiet([]);
        if (onPopulateDetailRows) onPopulateDetailRows([]);

        const matchedEndpoint = GetEndPoints.find(item => item.ma_ct === chungTuGoc.ma_ct);

        if (!matchedEndpoint) {
          console.warn(`No endpoint found for ma_ct: ${chungTuGoc.ma_ct}`);
          return;
        }

        const fetchDetailByUuid = async (ma_ct: string, uuid: string) => {
          if (ma_ct === 'PK1') {
            return await getPhieuKeToanDetail(uuid);
          }
          if (ma_ct === 'CN5') {
            return await getChungTuPhaiTraKhacDetail(uuid);
          }
          if (ma_ct === 'CN6') {
            return await getButToanDieuChinhGiamCongNoDetail(uuid);
          }
          return [];
        };

        let uuid = idGoc || chungTuGoc?.uuid || '';
        if (!uuid) {
          const response = await api.get(`/entities/${entity.slug}/erp/${matchedEndpoint.endPoint}/`);
          const results = response?.data?.results || [];
          const matched = results.find((doc: any) => doc?.so_ct === chungTuGoc.so_ct);
          uuid = matched?.uuid || matched?.id || '';
        }

        if (!uuid) {
          actions.setChungTuChiTiet([]);
          actions.setIdGoc('');
          if (onPopulateDetailRows) onPopulateDetailRows([]);
          return;
        }

        const detailData = await fetchDetailByUuid(chungTuGoc.ma_ct, uuid);
        actions.setChungTuChiTiet(detailData || []);
        actions.setIdGoc(uuid);

        if (onPopulateDetailRows) {
          onPopulateDetailRows(detailData || []);
        }
      } catch (error) {
        console.error('Error fetching document details:', error);
        actions.setChungTuChiTiet([]);
      }
    },
    [
      actions,
      entity?.slug,
      onPopulateDetailRows,
      getPhieuKeToanDetail,
      getChungTuPhaiTraKhacDetail,
      getButToanDieuChinhGiamCongNoDetail
    ]
  );

  const handleChungTuGocSelection = useCallback(
    async (chungTuGoc: any) => {
      actions.setChungTuGoc(chungTuGoc);
      await loadChungTuGocDetails(chungTuGoc);
    },
    [actions, loadChungTuGocDetails]
  );

  useEffect(() => {
    if (formMode === 'edit' && state.chungTuGoc && state.id_goc) {
      loadChungTuGocDetails(state.chungTuGoc, state.id_goc);
    }
  }, [formMode, state.chungTuGoc, state.id_goc, loadChungTuGocDetails]);

  return (
    <div className='flex flex-col gap-3 p-4'>
      <div className='grid grid-cols-12 gap-6'>
        <div className='col-span-9 space-y-4'>
          <div className='flex items-center'>
            <Label className='w-[150px]'>Loại c/từ</Label>
            <FormField
              name='ma_ngv'
              type='select'
              options={[
                { value: '1', label: '1. Chứng từ ghi âm' },
                { value: '2', label: '2. Chứng từ đảo' }
              ]}
              disabled={formMode === 'view'}
              onValueChange={e => actions.setMaNgv(String(e.target?.value || e))}
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-[150px]'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField
                name='dien_giai'
                type='text'
                value={state.dienGiai}
                onValueChange={e => actions.setDienGiai(String(e.target?.value || e))}
                disabled={formMode === 'view'}
                labelClassName='w-48'
                className='w-full'
              />
            </div>
          </div>
          <div className='flex items-center'>
            <Label className='w-[150px]'>Chứng từ gốc</Label>
            <SearchField
              searchColumns={chungTuKeToanSearchColumns}
              columnDisplay='so_ct'
              displayRelatedField='ten_ct'
              dialogTitle='Chứng từ kế toán'
              value={state.chungTuGoc?.so_ct || ''}
              relatedFieldValue={state.chungTuGoc?.ten_ct || ''}
              onRowSelection={handleChungTuGocSelection}
              searchEndpoint={`/${QUERY_KEYS.CHUNG_TU_KE_TOAN}`}
              disabled={formMode === 'view'}
            />
          </div>
        </div>

        <div className='col-span-3 space-y-4'>
          <div className='flex items-center'>
            <DocumentNumberField
              ma_ct={MA_CHUNG_TU.TONG_HOP.BUT_TOAN_HUY_DAO}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              disabled={formMode === 'view'}
              classNameSearchField='w-full'
              labelClassName='w-32'
              formMode={formMode}
            />
          </div>
          <div className='flex items-center'>
            <Label className='w-32'>Ngày chứng từ</Label>
            <FormField name='ngay_ct' type='date' disabled={formMode === 'view'} labelClassName='w-32 shrink-0' />
          </div>

          <div className='flex'>
            <CurrencyInput formMode={formMode} onExchangeRateChange={handleExchangeRateChange} />
          </div>

          <div className='flex items-center'>
            <Label className='w-32'>Trạng thái</Label>
            <FormField
              name='status'
              type='select'
              disabled={formMode === 'view'}
              inputClassName='w-40'
              options={[
                { value: '0', label: 'Chưa ghi sổ' },
                { value: '5', label: 'Đã ghi sổ' }
              ]}
            />
          </div>

          <FormField
            label='Dữ liệu được nhận'
            name='transfer_yn'
            type='checkbox'
            disabled={true}
            labelClassName='w-32'
            defaultValue={false}
          />
        </div>
      </div>
    </div>
  );
}
